import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList
} from "@wingspanhq/fe-component-library";
import { BatchType } from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";
import { useMemo } from "react";

import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import {
  routeHelpersImportPayablesBatch,
  routeHelpersImportPayablesStart
} from "../../paths";
import { buildPayableImportBatchFiltersConfig } from "./filters/config";
import { PayableImportBatchFilter, STATUS_FILTER_ALL } from "./filters/types";
import { TablePayableImportBatches } from "./TablePayableImportBatches";

export const FilterableTablePayableImportBatches: React.FC = () => {
  const { urlQuery, setUrlQuery } = useUrlQuery<PayableImportBatchFilter>();
  const filtersConfig = useMemo(
    () => buildPayableImportBatchFiltersConfig(),
    []
  );
  const goToBatch = routeHelpersImportPayablesBatch.useGoTo();
  const goToStart = routeHelpersImportPayablesStart.useGoTo();

  const status =
    urlQuery.filter.status && urlQuery.filter.status !== STATUS_FILTER_ALL
      ? urlQuery.filter.status
      : undefined;
  const hasFilters = !!status;

  const queryBatches = queriesBatches.listBatches.usePaginatedQuery({
    filter: {
      type: { eq: BatchType.PAYABLE_IMPORT },
      ...(status ? { status: { anyOf: [status] } } : {})
    }
  });
  const batches = queryBatches.flatData ?? [];
  const totalCount = queryBatches.data?.pages?.[0]?.pagination?.totalSize;

  const renderTable = () => {
    if (queryBatches.isLoading) {
      return <TablePayableImportBatches batches={[]} loading />;
    }

    if (queryBatches.isError) {
      return (
        <WSEmptyState
          type="error"
          title="Could not load imports"
          description="Something went wrong loading your payable imports."
          actions={{
            buttons: [
              {
                label: "Try again",
                kind: "Secondary",
                onClick: () => queryBatches.refetch()
              }
            ]
          }}
        />
      );
    }

    if (batches.length === 0) {
      return hasFilters ? (
        <WSEmptyState
          type="search"
          title="No imports match your filters"
          description="Try clearing the import status filter."
          actions={{
            buttons: [
              {
                label: "Clear filters",
                kind: "Secondary",
                onClick: () => setUrlQuery(prev => ({ ...prev, filter: {} }))
              }
            ]
          }}
        />
      ) : (
        <WSEmptyState
          type="documents"
          title="No imports yet"
          description="Upload a spreadsheet to import payables and deductions in bulk."
          actions={{
            buttons: [
              {
                label: "Import payables & deductions",
                kind: "Secondary",
                onClick: () => goToStart()
              }
            ]
          }}
        />
      );
    }

    return (
      <WSInfiniteScroll
        onLoad={() => queryBatches.fetchNextPage()}
        loadMore={batches.length > 0}
        endOfList={!queryBatches.hasNextPage}
        loading={!!queryBatches.isFetchingNextPage}
      >
        <TablePayableImportBatches
          batches={batches}
          onRowClick={batch => goToBatch({ batchId: batch.id as string })}
        />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="2XL">
      <WSFilters<PayableImportBatchFilter>
        values={urlQuery.filter}
        onFilter={filter => setUrlQuery(prev => ({ ...prev, filter }))}
        primaryFilters={filtersConfig}
        info={totalCount === undefined ? undefined : { count: totalCount }}
      />
      {renderTable()}
    </WSList>
  );
};
